import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Input } from "@rakazo/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { privatePreviewPath } from "../lib/private-preview";
import { useSpaceQueries, useQueryUserId } from "../lib/query";
import { useSpaceRpc, useBoundSpaceId } from "../lib/space-rpc";
import { libraryListingRoot } from "../lib/library-listing";
import { selectedSpaceId } from "../lib/rpc";

export function PageSharing({ artifactId, canBeApp }: { artifactId: string; canBeApp?: boolean }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const queries = useSpaceQueries();
  const client = useSpaceRpc();
  const userId = useQueryUserId();
  const key = ["page-sharing", userId, artifactId];
  const status = useQuery({
    queryKey: key,
    queryFn: () => client.artifacts.sharePage({ artifactId, action: "status" }),
  });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState<"publish" | "password" | null>(null);
  const [protect, setProtect] = useState(false);
  const [password, setPassword] = useState("");
  async function change(
    action: "publish" | "unpublish" | "setPassword" | "removePassword",
    secret?: string,
  ) {
    if (
      action === "removePassword" &&
      !window.confirm(
        t`Remove password protection? Anyone with the link will be able to view this page.`,
      )
    )
      return;
    setBusy(true);
    setNotice("");
    try {
      const result = await client.artifacts.sharePage({
        artifactId,
        action,
        ...(secret !== undefined ? { password: secret } : {}),
      });
      queryClient.setQueryData(key, result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["page-sharing"] }),
        queryClient.invalidateQueries({ queryKey: queries.artifacts.listSpace.key() }),
        queryClient.invalidateQueries({ queryKey: libraryListingRoot(userId) }),
      ]);
      setEditing(null);
      setPassword("");
      setNotice(
        action === "publish"
          ? t`Published`
          : action === "unpublish"
            ? t`Private`
            : t`Password settings saved`,
      );
    } catch {
      setNotice(t`Could not update sharing. Please try again.`);
    } finally {
      setBusy(false);
    }
  }
  async function makeApp() {
    if (
      status.data?.path &&
      !window.confirm(
        t`Move to My Apps? This will make the item private and disable its public link.`,
      )
    )
      return;
    setBusy(true);
    setNotice("");
    try {
      await client.artifacts.enableApp({ artifactId });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: queries.artifacts.listSpace.key() }),
        queryClient.invalidateQueries({ queryKey: libraryListingRoot(userId) }),
        queryClient.invalidateQueries({ queryKey: queries.artifacts.getById.key() }),
      ]);
    } catch {
      setNotice(t`Could not move this item to My Apps. Please try again.`);
    } finally {
      setBusy(false);
    }
  }
  if (status.data?.internalApp)
    return (
      <span className="text-base text-muted-foreground">
        <Trans>Private app</Trans>
      </span>
    );
  return (
    <div className="flex flex-wrap items-center gap-2">
      {status.data?.path ? (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(
                  new URL(status.data!.path!, window.location.origin).href,
                );
                setNotice(t`Link copied`);
              } catch {
                setNotice(t`Could not copy link. Open the page and copy its address.`);
              }
            }}
          >
            <Trans>Copy link</Trans>
          </Button>
          {status.data.publishedArtifactId !== artifactId ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => change("publish")}>
              <Trans>Publish this version</Trans>
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              setEditing("password");
              setPassword("");
              setProtect(true);
            }}
          >
            {status.data.passwordProtected ? (
              <Trans>Change password</Trans>
            ) : (
              <Trans>Add password</Trans>
            )}
          </Button>
          {status.data.passwordProtected ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => change("removePassword")}
            >
              <Trans>Remove password</Trans>
            </Button>
          ) : null}
          <Button variant="outline" size="sm" disabled={busy} onClick={() => change("unpublish")}>
            <Trans>Unpublish</Trans>
          </Button>
        </>
      ) : (
        <Button
          variant="outline"
          size="sm"
          disabled={busy || !status.data}
          onClick={() => {
            setEditing("publish");
            setPassword("");
            setProtect(false);
          }}
        >
          <Trans>Publish page</Trans>
        </Button>
      )}
      {canBeApp && !editing ? (
        <Button variant="outline" size="sm" disabled={busy || !status.data} onClick={makeApp}>
          <Trans>Move to My Apps</Trans>
        </Button>
      ) : null}
      {editing ? (
        <form
          className="basis-full space-y-3 rounded-lg border border-border bg-card p-4"
          onSubmit={(event) => {
            event.preventDefault();
            change(
              editing === "publish" ? "publish" : "setPassword",
              editing === "password" || protect ? password : undefined,
            );
          }}
        >
          {editing === "publish" ? (
            <>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Anyone with the link can view the published version unless you require a password.
                </Trans>
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={protect}
                  onChange={(event) => setProtect(event.target.checked)}
                />
                <Trans>Require password</Trans>
              </label>
            </>
          ) : null}
          {editing === "password" || protect ? (
            <label className="block space-y-1 text-sm">
              <span>
                <Trans>New page password</Trans>
              </span>
              <Input
                type="password"
                autoComplete="new-password"
                minLength={8}
                maxLength={128}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={t`At least 8 characters`}
              />
            </label>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy}>
              {editing === "publish" ? <Trans>Publish</Trans> : <Trans>Save password</Trans>}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                setEditing(null);
                setPassword("");
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </div>
        </form>
      ) : null}
      {status.isError ? (
        <span role="alert" className="text-sm text-destructive">
          <Trans>Could not load sharing settings.</Trans>
        </span>
      ) : null}
      <span role="status" className="text-sm text-muted-foreground">
        {notice}
      </span>
    </div>
  );
}

export function PageOpenButton({ artifactId }: { artifactId: string }) {
  const boundSpaceId = useBoundSpaceId();
  return (
    <Button
      variant="outline"
      size="sm"
      nativeButton={false}
      render={
        <a
          href={privatePreviewPath(artifactId, boundSpaceId ?? selectedSpaceId())}
          target="_blank"
          rel="noopener noreferrer"
        />
      }
    >
      <Trans>Open in browser</Trans>
      <ArrowUpRight size={15} strokeWidth={1.9} aria-hidden="true" />
    </Button>
  );
}
